import { useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { 
  Users, FileText, Receipt, Package, Send, CreditCard, 
  Settings, ChevronLeft, ChevronRight, X, Sparkles,
  Mail, Upload, Calculator, Bell
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const walkthroughSteps = [
  {
    title: "Welcome to QuoteSorted",
    description: "Your all-in-one solution for managing customers, quotes, and invoices. Let's take a quick tour of the key features.",
    icon: Sparkles,
    color: "bg-blue-500",
    image: null,
    features: [
      "Professional quote & invoice management",
      "Customer database with easy access",
      "Automated calculations & tracking",
      "Email integration for seamless communication"
    ]
  },
  {
    title: "Customer Management",
    description: "Keep all your customer information organized in one place. Add contact details, addresses, and track their quote/invoice history.",
    icon: Users,
    color: "bg-green-500",
    features: [
      "Store customer contact details",
      "Track customer history",
      "Share registration links for self sign-up",
      "Quick search and filtering"
    ]
  },
  {
    title: "Create Professional Quotes",
    description: "Build detailed quotes with line items, materials costs, and automatic calculations. Convert accepted quotes to invoices with one click.",
    icon: FileText,
    color: "bg-purple-500",
    features: [
      "Add unlimited line items",
      "Include materials with markup",
      "Multiple status tracking (Draft, Sent, Accepted)",
      "PDF generation and email sending"
    ]
  },
  {
    title: "Supplier Quote Requests",
    description: "Request quotes from your suppliers directly through the platform. They can upload PDF quotes which are automatically parsed for pricing.",
    icon: Upload,
    color: "bg-orange-500",
    features: [
      "Email quote requests to suppliers",
      "Suppliers upload PDF quotes",
      "AI-powered price extraction",
      "Apply supplier prices with markup"
    ]
  },
  {
    title: "Invoice Management",
    description: "Create and send professional invoices. Track payments, send reminders, and manage your cash flow effectively.",
    icon: Receipt,
    color: "bg-indigo-500",
    features: [
      "Convert quotes to invoices instantly",
      "Track payment status",
      "Record partial payments",
      "Auto-update status when paid"
    ]
  },
  {
    title: "Item Catalog",
    description: "Build a library of your commonly used items and services. Quickly add them to quotes and invoices with consistent pricing.",
    icon: Package,
    color: "bg-teal-500",
    features: [
      "Create reusable item templates",
      "Set default prices",
      "Bulk add items to documents",
      "Organize by categories"
    ]
  },
  {
    title: "Email Integration",
    description: "Send quotes and invoices directly to your customers via email. Professional templates with your company branding.",
    icon: Mail,
    color: "bg-pink-500",
    features: [
      "Send quotes & invoices via email",
      "Customizable email templates",
      "Track when emails are sent",
      "Include PDF attachments"
    ]
  },
  {
    title: "Online Payments",
    description: "Accept payments online through Square. Customers can pay invoices directly with a secure payment link.",
    icon: CreditCard,
    color: "bg-emerald-500",
    features: [
      "Square payment integration",
      "Secure payment links",
      "Automatic payment recording",
      "Optional fee markup"
    ]
  },
  {
    title: "Notifications & Tracking",
    description: "Stay on top of your business with real-time notifications for accepted quotes, paid invoices, and supplier responses.",
    icon: Bell,
    color: "bg-amber-500",
    features: [
      "Quote acceptance alerts",
      "Payment notifications",
      "Supplier quote ready alerts",
      "Support ticket updates"
    ]
  },
  {
    title: "Customize Your Settings",
    description: "Personalize QuoteSorted with your company details, logo, and preferences. Make every document reflect your brand.",
    icon: Settings,
    color: "bg-gray-500",
    features: [
      "Company branding & logo",
      "Custom invoice terms",
      "Manage suppliers list",
      "Payment preferences"
    ]
  }
];

const WalkthroughGuide = ({ open, onOpenChange }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const step = walkthroughSteps[currentStep];
  const Icon = step.icon;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === walkthroughSteps.length - 1;
  
  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep(currentStep + 1);
    } else {
      onOpenChange(false);
      setCurrentStep(0);
    }
  };
  
  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleClose = () => {
    onOpenChange(false);
    setCurrentStep(0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden" data-testid="walkthrough-dialog">
        {/* Header with gradient */}
        <div className={`${step.color} p-6 text-white relative`}>
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
            data-testid="walkthrough-close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Icon className="w-8 h-8" />
            </div>
            <div>
              <p className="text-white/70 text-sm">Step {currentStep + 1} of {walkthroughSteps.length}</p>
              <h2 className="text-2xl font-bold">{step.title}</h2>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6">{step.description}</p>
          
          {/* Features list */}
          <div className="space-y-3 mb-6">
            {step.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${step.color}`} />
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
          
          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6">
            {walkthroughSteps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep 
                    ? `${step.color} w-6` 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                data-testid={`walkthrough-dot-${index}`}
              />
            ))}
          </div>
          
          {/* Navigation buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={isFirstStep}
              className="gap-2"
              data-testid="walkthrough-prev"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            
            <Button
              onClick={handleNext}
              className={`gap-2 ${step.color} hover:opacity-90 border-0 text-white`}
              data-testid="walkthrough-next"
            >
              {isLastStep ? "Get Started" : "Next"}
              {!isLastStep && <ChevronRight className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WalkthroughGuide;
